<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);

const { t } = useI18n();
const store = useStore();

const selectedWebhookId = ref(null);
const selectedInboxId = ref(null);
const selectedTemplate = ref(null);
const templateSearch = ref('');
const selectedFile = ref(null);
const isDragging = ref(false);
const isLoading = ref(false);
const fileInputRef = ref(null);
const templateParserRef = ref(null);

const prospectWebhooks = useMapGetter('prospectWebhooks/getProspectWebhooks');
const allInboxes = useMapGetter('inboxes/getInboxes');
const getFilteredTemplates = useMapGetter(
  'inboxes/getFilteredWhatsAppTemplates'
);

const selectedWebhook = computed(() =>
  prospectWebhooks.value.find(w => w.id === selectedWebhookId.value)
);

const selectedInbox = computed(() =>
  allInboxes.value.find(i => i.id === selectedInboxId.value)
);

const isWhatsAppInbox = computed(
  () => selectedInbox.value?.channel_type === INBOX_TYPES.WHATSAPP
);

const inboxTemplates = computed(() => {
  if (!selectedInboxId.value) return [];
  return getFilteredTemplates.value(selectedInboxId.value);
});

const filteredTemplates = computed(() => {
  if (!templateSearch.value) return inboxTemplates.value;
  return inboxTemplates.value.filter(tmpl =>
    tmpl.name.toLowerCase().includes(templateSearch.value.toLowerCase())
  );
});

const getTemplateBody = template => {
  const body = template.components.find(c => c.type === 'BODY');
  return body?.text || '';
};

const handleTemplateSelect = template => {
  selectedTemplate.value = template;
};

const handleClearTemplate = () => {
  selectedTemplate.value = null;
  templateSearch.value = '';
};

const canConfirm = computed(() => {
  if (!selectedWebhookId.value || !selectedFile.value) return false;
  if (selectedTemplate.value && templateParserRef.value?.v$?.$invalid)
    return false;
  return true;
});

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];

const isValidFile = file => {
  const name = file.name.toLowerCase();
  return ACCEPTED_EXTENSIONS.some(ext => name.endsWith(ext));
};

const formatFileSize = bytes => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const handleFileChange = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  if (!isValidFile(file)) {
    useAlert(t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.INVALID_FILE'));
    return;
  }
  selectedFile.value = file;
};

const handleDrop = event => {
  isDragging.value = false;
  const file = event.dataTransfer?.files?.[0];
  if (!file) return;
  if (!isValidFile(file)) {
    useAlert(t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.INVALID_FILE'));
    return;
  }
  selectedFile.value = file;
};

const clearFile = () => {
  selectedFile.value = null;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

const handleConfirm = async () => {
  if (!canConfirm.value) return;
  isLoading.value = true;
  try {
    const formData = new FormData();
    formData.append('file', selectedFile.value, selectedFile.value.name);

    if (selectedInboxId.value) {
      formData.append('inbox_id', selectedInboxId.value);
    }

    if (selectedTemplate.value) {
      const templateData = {
        name: selectedTemplate.value.name,
        language: selectedTemplate.value.language,
        namespace: selectedTemplate.value.namespace,
        category: selectedTemplate.value.category,
      };
      const params = templateParserRef.value?.processedParams;
      if (params && Object.keys(params).length > 0) {
        templateData.processed_params = params;
      }
      formData.append('template', JSON.stringify(templateData));
    }

    await fetch(selectedWebhook.value.url, { method: 'POST', body: formData });
    useAlert(t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.SUCCESS'));
    emit('close');
  } catch {
    useAlert(t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const reset = () => {
  selectedWebhookId.value = null;
  selectedInboxId.value = null;
  selectedTemplate.value = null;
  templateSearch.value = '';
  selectedFile.value = null;
  isLoading.value = false;
  isDragging.value = false;
  if (fileInputRef.value) fileInputRef.value.value = '';
};

watch(selectedInboxId, () => {
  selectedTemplate.value = null;
  templateSearch.value = '';
});

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      store.dispatch('prospectWebhooks/get');
      reset();
    }
  }
);
</script>

<template>
  <woot-modal :show="show" :on-close="() => emit('close')">
    <div class="h-auto overflow-auto flex flex-col w-[30rem]">
      <woot-modal-header
        :header-title="t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TITLE')"
        :header-content="t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.SUBTITLE')"
      />
      <div class="flex flex-col gap-4 px-8 pb-8 pt-4">
        <!-- Webhook selector -->
        <label>
          {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.WEBHOOK_LABEL') }}
          <span class="required-asterisk">*</span>
          <select v-model="selectedWebhookId" class="mt-1">
            <option :value="null" disabled>
              {{
                t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.WEBHOOK_PLACEHOLDER')
              }}
            </option>
            <option
              v-for="webhook in prospectWebhooks"
              :key="webhook.id"
              :value="webhook.id"
            >
              {{ webhook.name }}
            </option>
          </select>
          <span v-if="!prospectWebhooks.length" class="message">
            {{ t('CONTACTS_BULK_ACTIONS.NO_WEBHOOKS') }}
          </span>
        </label>

        <!-- Inbox selector -->
        <label>
          {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.INBOX_LABEL') }}
          <select v-model="selectedInboxId" class="mt-1">
            <option :value="null">
              {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.INBOX_PLACEHOLDER') }}
            </option>
            <option
              v-for="inbox in allInboxes"
              :key="inbox.id"
              :value="inbox.id"
            >
              {{ inbox.name }}
            </option>
          </select>
        </label>

        <!-- WhatsApp template section -->
        <template v-if="isWhatsAppInbox">
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_LABEL') }}
              </span>
              <button
                v-if="selectedTemplate"
                class="text-xs text-n-brand hover:underline"
                @click="handleClearTemplate"
              >
                {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_CHANGE') }}
              </button>
            </div>

            <!-- Selected template: parser with variable inputs -->
            <WhatsAppTemplateParser
              v-if="selectedTemplate"
              ref="templateParserRef"
              :template="selectedTemplate"
              @reset-template="handleClearTemplate"
            />

            <!-- Template list -->
            <div v-else class="flex flex-col gap-2">
              <div class="relative">
                <span
                  class="i-lucide-search absolute size-3.5 top-2.5 left-3 text-n-slate-10 pointer-events-none"
                />
                <input
                  v-model="templateSearch"
                  type="search"
                  class="placeholder:text-n-slate-9 pl-8"
                  :placeholder="
                    t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_SEARCH')
                  "
                />
              </div>
              <div
                class="max-h-44 overflow-y-auto flex flex-col divide-y divide-n-weak rounded-lg border border-n-weak"
              >
                <button
                  v-for="template in filteredTemplates"
                  :key="template.id || template.name"
                  class="flex flex-col gap-1 px-3 py-2.5 text-left hover:bg-n-alpha-2 first:rounded-t-lg last:rounded-b-lg"
                  @click="handleTemplateSelect(template)"
                >
                  <div class="flex items-center justify-between">
                    <span class="text-sm font-medium text-n-slate-12">
                      {{ template.name }}
                    </span>
                    <span class="text-xs text-n-slate-10">
                      {{ template.language }}
                    </span>
                  </div>
                  <p class="text-xs text-n-slate-11 line-clamp-2">
                    {{ getTemplateBody(template) }}
                  </p>
                </button>
                <div
                  v-if="!filteredTemplates.length"
                  class="px-3 py-4 text-sm text-n-slate-11 text-center"
                >
                  {{ t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.TEMPLATE_EMPTY') }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Spreadsheet format hint -->
        <div
          class="flex flex-col gap-1.5 rounded-lg border border-n-weak bg-n-alpha-1 px-3 py-2.5"
        >
          <div class="flex items-center gap-1.5">
            <span
              class="i-lucide-info size-3.5 text-n-slate-10 flex-shrink-0"
            />
            <span class="text-xs font-medium text-n-slate-11">
              {{ t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_LABEL') }}
            </span>
          </div>
          <p class="text-xs text-n-slate-10">
            {{ t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_HINT') }}
          </p>
          <div
            class="flex rounded border border-n-weak overflow-hidden text-xs"
          >
            <div
              class="flex-1 border-r border-n-weak bg-n-alpha-2 px-3 py-1.5 font-semibold text-n-slate-12 text-center"
            >
              {{
                t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_COL_NAME')
              }}
            </div>
            <div
              class="flex-1 bg-n-alpha-2 px-3 py-1.5 font-semibold text-n-slate-12 text-center"
            >
              {{
                t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_COL_PHONE')
              }}
            </div>
          </div>
          <div
            class="flex rounded border border-n-weak overflow-hidden text-xs text-n-slate-10"
          >
            <div class="flex-1 border-r border-n-weak px-3 py-1 text-center">
              {{
                t(
                  'CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_EXAMPLE_NAME'
                )
              }}
            </div>
            <div class="flex-1 px-3 py-1 text-center">
              {{
                t(
                  'CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.TEMPLATE_EXAMPLE_PHONE'
                )
              }}
            </div>
          </div>
        </div>

        <!-- File upload -->
        <div class="flex flex-col gap-1.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.FILE_LABEL') }}
            <span class="text-n-ruby-9">*</span>
          </span>

          <!-- Selected file preview -->
          <div
            v-if="selectedFile"
            class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-alpha-2 px-3 py-3"
          >
            <span
              class="i-lucide-file-spreadsheet size-6 text-n-brand flex-shrink-0"
            />
            <div class="flex flex-col min-w-0 flex-1">
              <span class="text-sm font-medium text-n-slate-12 truncate">
                {{ selectedFile.name }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{ formatFileSize(selectedFile.size) }}
              </span>
            </div>
            <button
              class="flex-shrink-0 p-1 rounded text-n-slate-10 hover:text-n-ruby-9 hover:bg-n-ruby-9/10 transition-colors"
              @click="clearFile"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>

          <!-- Drop zone -->
          <div
            v-else
            class="flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-8 cursor-pointer transition-all duration-150 select-none"
            :class="
              isDragging
                ? 'border-n-brand bg-n-brand/5 scale-[1.01]'
                : 'border-n-weak hover:border-n-brand/60 hover:bg-n-alpha-1'
            "
            @click="fileInputRef?.click()"
            @dragover.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @drop.prevent="handleDrop"
          >
            <span
              class="i-lucide-upload-cloud size-10 transition-colors"
              :class="isDragging ? 'text-n-brand' : 'text-n-slate-9'"
            />
            <div class="flex flex-col items-center gap-1">
              <p class="text-sm font-medium text-n-slate-12">
                {{ t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.DROP_HINT') }}
              </p>
              <p class="text-xs text-n-slate-10">
                {{
                  t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.ACCEPTED_FORMATS')
                }}
              </p>
            </div>
          </div>

          <input
            ref="fileInputRef"
            type="file"
            accept=".xlsx,.xls,.csv"
            class="hidden"
            @change="handleFileChange"
          />
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-2 pt-2">
          <Button
            faded
            slate
            :label="t('CONTACTS_BULK_ACTIONS.DISPATCH_MODAL.CANCEL')"
            @click="emit('close')"
          />
          <Button
            :label="t('CONTACTS_BULK_ACTIONS.SPREADSHEET_MODAL.CONFIRM')"
            :disabled="!canConfirm"
            :is-loading="isLoading"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </woot-modal>
</template>
